import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip as StatusChip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { customerApi, orderApi } from '../services/api';
import { Customer, CustomerFormData, Order, OrderPaginationParams } from '../types';

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    phone: '',
    email: '',
    address: '',
  });
  
  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [ordersTotal, setOrdersTotal] = useState(0);

  useEffect(() => {
    if (id) {
      loadCustomer(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    if (customer) {
      loadOrders();
    }
  }, [customer, ordersPage, ordersRowsPerPage]);

  const loadCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerApi.getById(customerId);
      if (response.status === 'success' && response.data) {
        setCustomer(response.data);
        setFormData({
          name: response.data.name,
          phone: response.data.phone,
          email: response.data.email || '',
          address: response.data.address || '',
        });
      } else {
        setError(response.message || 'Customer not found');
      }
    } catch (err) {
      setError('Failed to load customer details');
      console.error('Load customer error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!customer) return;
    
    try {
      setOrdersLoading(true);
      setOrdersError(null);
      const params: OrderPaginationParams = {
        page: ordersPage + 1,
        limit: ordersRowsPerPage,
        sort_by: 'order_date',
        sort_order: 'DESC'
      };
      const response = await orderApi.getByCustomer(customer.id, params);
      if (response.status === 'success') {
        setOrders(response.data || []);
        setOrdersTotal((response as any).pagination?.total_records || 0);
      } else {
        setOrdersError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      setOrdersError('Failed to load orders');
      console.error('Load orders error:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleCreateNewOrder = () => {
    if (customer) {
      navigate('/new-order', { state: { selectedCustomer: customer } });
    }
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const handleOrdersPageChange = (event: unknown, newPage: number) => {
    setOrdersPage(newPage);
  };

  const handleOrdersRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrdersRowsPerPage(parseInt(event.target.value, 10));
    setOrdersPage(0);
  };

  const handleEditSubmit = async () => {
    if (!customer || !formData.name.trim() || !formData.phone.trim()) {
      setError('Name and phone are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await customerApi.update(customer.id, formData);
      if (response.status === 'success') {
        setCustomer(prev => prev ? { ...prev, ...formData } : null);
        setShowEditDialog(false);
      } else {
        setError(response.message || 'Failed to update customer');
      }
    } catch (err) {
      setError('Failed to update customer');
      console.error('Update customer error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!customer) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await customerApi.delete(customer.id);
      if (response.status === 'success') {
        navigate('/customers');
      } else {
        setError(response.message || 'Failed to delete customer');
      }
    } catch (err) {
      setError('Failed to delete customer');
      console.error('Delete customer error:', err);
    } finally {
      setSubmitting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRestore = async () => {
    if (!customer) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await customerApi.restore(customer.id);
      if (response.status === 'success') {
        navigate('/customers');
      } else {
        setError(response.message || 'Failed to restore customer');
      }
    } catch (err) {
      setError('Failed to restore customer');
      console.error('Restore customer error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/customers')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const isDeleted = customer.deleted === true;

  // Get the last order date from orders array
  const getLastOrderDate = () => {
    if (orders.length === 0) return 'No orders yet';
    // Orders are already sorted by order_date DESC, so first order is the most recent
    return new Date(orders[0].order_date).toLocaleDateString();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/customers')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          {isDeleted && (
            <Chip 
              label="Deleted" 
              color="error" 
              size="small" 
              sx={{ ml: 2 }} 
            />
          )}
        </Box>
        <Box>
          {isDeleted ? (
            <Button
              variant="contained"
              startIcon={<RestoreIcon />}
              onClick={handleRestore}
              disabled={submitting}
              color="success"
            >
              Restore Customer
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNewOrder}
                sx={{ mr: 1 }}
                color="primary"
              >
                Create New Order
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                color="error"
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="h6">
                        {customer.name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="h6">
                        {customer.phone}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              {customer.email && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="h6">
                          {customer.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              
              {customer.address && (
                <Box>
                  <Box display="flex" alignItems="flex-start" mb={2}>
                    <LocationIcon sx={{ mr: 1, color: 'text.secondary', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="h6">
                        {customer.address}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    OnBoard Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(customer.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Order Date
                  </Typography>
                  <Typography variant="body1">
                    {getLastOrderDate()}
                  </Typography>
                </Box>
              </Box>
              
              {isDeleted && (
                <Box display="flex" alignItems="center" mb={2}>
                  <CalendarIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body1" color="error.main">
                      Deleted
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Orders Section */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Customer Orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ordersTotal} total orders
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {ordersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOrdersError(null)}>
              {ordersError}
            </Alert>
          )}
          
          {ordersLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Delivery Date</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow 
                        key={order.id}
                        hover
                        onClick={() => order.id && handleViewOrder(order.id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {order.order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(order.order_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Not set'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            ₹{order.total_amount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={order.status}
                            color={
                              order.status === 'completed' ? 'success' :
                              order.status === 'in_progress' ? 'warning' :
                              order.status === 'pending' ? 'default' :
                              'error'
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {orders.length === 0 && !ordersLoading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box py={3}>
                            <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="textSecondary">
                              No orders found for this customer
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {ordersTotal > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={ordersTotal}
                  rowsPerPage={ordersRowsPerPage}
                  page={ordersPage}
                  onPageChange={handleOrdersPageChange}
                  onRowsPerPageChange={handleOrdersRowsPerPageChange}
                />
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this customer? This action can be undone by restoring the customer from the settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetails;
